import React, { forwardRef, useState } from "react";
import {
  useMantineTheme,
  Card,
  Group,
  Avatar,
  Text,
  Tooltip,
  ActionIcon,
  Flex,
} from "@mantine/core";

import { CustomSelect } from "@components/custom";

import styles from "@scss/custom-components/avatar-card.module.scss";

interface Option {
  value: string;
  label: string;
  image: string;
}

interface CustomAvatarCardProps {
  options: Option[];
  user: { name: string; role: string; avatar: string };
  onImpersonate: () => void;
  onChange?: (value: string) => void;
}

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  label: string;
  image: string;
  checked?: boolean;
}

const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
  ({ image, label, checked, ...others }, ref) => (
    <div ref={ref} {...others} className={styles.selectItem}>
      <Group>
        <Avatar
          src={image}
          size="md"
          radius="xl"
          classNames={{ root: styles.companyAvatarRootDropdown }}
        />
        <Text size="sm">{label}</Text>
      </Group>
    </div>
  ),
);
const selectStyles = {
  root: styles.selectRoot,
  dropdown: styles.selectDropdown,
  item: styles.selectItem,
  icon: styles.selectIcon,
  input: styles.selectInput,
  rightSection: styles.selectRightSection,
};

export function CustomAvatarCard({
  options,
  user,
  onImpersonate,
  onChange,
}: CustomAvatarCardProps) {
  const theme = useMantineTheme();
  const [selected, setSelected] = useState(options[0]?.value || "");
  const current = options.find((o) => o.value === selected);

  const handleChange = (val: string | null) => {
    if (val !== null) {
      setSelected(val);
      onChange?.(val);
    }
  };

  return (
    <Card withBorder radius="md" p="md" className={styles.avatarCard}>
      <CustomSelect
        data={options}
        value={selected}
        onChange={handleChange}
        renderOption={({ option, checked }) => (
          <SelectItem image={""} {...option} checked={checked} />
        )}
        variant="darker"
        placeholder="Select option"
        classNames={selectStyles}
        leftSection={
          current ? (
            <Avatar
              src={current.image}
              size="md"
              radius="xl"
              classNames={{
                root: styles.companyAvatarRoot,
                image: styles.companyAvatarImage,
              }}
            />
          ) : null
        }
        rightSection={
          <></>
        }
      />

      <Group align="center" mt="md" className={styles.userAvatarWrapper}>
        <Flex justify="space-between" align="center" style={{ width: "100%" }}>
          <Group gap="sm">
            <Avatar
              src={user.avatar}
              radius="xl"
              size="md"
              className={styles.userAvatar}
            />
            <div className={styles.userInfo}>
              <Text className={styles.userName}>{user.name}</Text>
              <Text className={styles.userRole} size="xs">
                {user.role}
              </Text>
            </div>
          </Group>
          <Tooltip label="Impersonate user" position="bottom">
            <ActionIcon
              className={styles.impersonateIcon}
              onClick={onImpersonate}
            >
              <></>
            </ActionIcon>
          </Tooltip>
        </Flex>
      </Group>
    </Card>
  );
}
